import { ITopicSection } from "@/api/topics";
import { ChevronRight, ExpandMore } from "@mui/icons-material";
import {
    Box,
    Chip,
    Collapse,
    IconButton,
    styled,
    Typography,
} from "@mui/material";
import { useState } from "react";
import { ChecklistClausesList } from "./ChecklistClausesList";

const SectionHeader = styled(Box)<{ isExpanded?: boolean }>(
    ({ isExpanded = false, theme }) => ({
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: isExpanded ? "8px 8px 0 0" : "8px",
        padding: "12px 16px",
        paddingRight: "12px",
        marginBottom: isExpanded ? 0 : "10px",
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        transition: "all 0.2s ease",
    })
);

const ContentContainer = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderTop: "none",
    borderRadius: "0 0 8px 8px",
    marginBottom: "10px",
    marginTop: 0,
    overflow: "hidden",
}));

interface ChecklistSectionAccordionProps {
    section: ITopicSection;
}

/**
 * Displays a document section type as an expandable section.
 * Styled to match ScopeTopicsV2 ExpandableDocumentSection pattern.
 * Shows section name with clause count chip and renders lists inside.
 * Expanded by default.
 */
export const ChecklistSectionAccordion = ({
    section,
}: ChecklistSectionAccordionProps) => {
    const [isExpanded, setIsExpanded] = useState(true);

    const sectionName = section.document_section_type?.name ?? "General";
    const clauseCount = section.clauses.length;

    // Don't render empty sections
    if (clauseCount === 0) {
        return null;
    }

    const chipLabel = `${clauseCount} clause${clauseCount !== 1 ? "s" : ""}`;

    const handleToggle = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <Box sx={{ width: "100%" }}>
            <SectionHeader isExpanded={isExpanded} onClick={handleToggle}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        gap: "0.5rem",
                        maxWidth: "calc(100% - 40px)",
                    }}
                >
                    <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ lineHeight: 1.4 }}
                    >
                        {sectionName}
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                        }}
                    >
                        <Chip
                            label={chipLabel}
                            size="small"
                            sx={{
                                bgcolor: "rgba(255, 255, 255, 0.2)",
                                color: "inherit",
                                fontWeight: 500,
                                fontSize: "0.75rem",
                            }}
                        />
                    </Box>
                </Box>

                <IconButton
                    size="small"
                    sx={{
                        color: (theme) => theme.palette.primary.contrastText,
                        padding: "4px",
                        flexShrink: 0,
                        ml: 1,
                        mr: 0,
                        "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                        },
                        "& .MuiSvgIcon-root": {
                            fontSize: "1.25rem",
                        },
                    }}
                >
                    {isExpanded ? <ExpandMore /> : <ChevronRight />}
                </IconButton>
            </SectionHeader>

            <Collapse in={isExpanded}>
                <ContentContainer>
                    <ChecklistClausesList clauses={section.clauses} />
                </ContentContainer>
            </Collapse>
        </Box>
    );
};
